import type { AppointmentTypeDto, AppointmentTypeInput } from '@aimmo/contracts';
import { appointmentTypeCreateSchema } from '@aimmo/contracts';
import { defineResource, tsRestResourceClient } from '@aimmo/ui';
import { api } from '../lib/api.js';

/**
 * Calendar appointment kinds (docs/domain/03-lookups-and-enums.md).
 *
 * The colours are free hex rather than the categorical scale: the calendar
 * paints events with them and the legacy lookup carried the same two fields.
 * A field showing the *word* "#b4552f" answers nothing, so both the cell and
 * the input render the colour itself.
 */
function ColorChip({
  color,
  textColor,
  label,
}: {
  color: string;
  textColor: string;
  label: string;
}) {
  return (
    <span
      className="inline-flex items-center rounded-pill px-2 py-0.5 text-label uppercase tracking-[0.08em]"
      style={{ backgroundColor: color, color: textColor }}
    >
      {label}
    </span>
  );
}

/** Native colour input plus the hex, because staff paste brand hex codes too. */
function colorField(label: string, name: 'color' | 'textColor') {
  return {
    name,
    label,
    type: 'custom' as const,
    render: ({
      value,
      onChange,
      onBlur,
      disabled,
    }: {
      value: unknown;
      onChange: (value: unknown) => void;
      onBlur: () => void;
      disabled: boolean;
    }) => {
      const current = typeof value === 'string' ? value : '#000000';
      return (
        <div className="flex items-center gap-2">
          <input
            type="color"
            aria-label={label}
            value={current}
            disabled={disabled}
            onChange={(event) => onChange(event.target.value)}
            onBlur={onBlur}
            className="h-9 w-12 cursor-pointer rounded-md border border-border bg-surface p-1"
          />
          <input
            type="text"
            value={current}
            disabled={disabled}
            onChange={(event) => onChange(event.target.value)}
            onBlur={onBlur}
            className="h-9 w-32 rounded-md border border-border bg-surface px-2 font-mono text-body"
          />
        </div>
      );
    },
  };
}

export const appointmentTypesResource = defineResource<
  AppointmentTypeDto,
  AppointmentTypeInput,
  AppointmentTypeInput,
  Partial<AppointmentTypeInput>
>({
  key: 'appointment-types',
  label: { one: 'Tipo appuntamento', many: 'Tipi appuntamento', gender: 'm' },
  client: tsRestResourceClient(api.appointmentTypes),
  getRowId: (row) => row.id,
  defaultState: { sort: 'sortOrder', order: 'asc' },
  search: { placeholder: 'Cerca per codice o etichetta…' },
  columns: [
    { id: 'code', header: 'Codice', sortKey: 'code', width: '8rem' },
    { id: 'label', header: 'Etichetta', sortKey: 'label' },
    {
      id: 'color',
      header: 'Colore',
      width: '12rem',
      cell: ({ row }) => (
        <ColorChip color={row.color} textColor={row.textColor} label={row.label} />
      ),
    },
    { id: 'sortOrder', header: 'Ordine', sortKey: 'sortOrder', align: 'right', width: '7rem' },
    { id: 'active', header: 'Attivo', width: '7rem' },
  ],
  filters: [
    { id: 'active', label: 'Stato', type: 'boolean', trueLabel: 'Attivo', falseLabel: 'Disattivo' },
  ],
  form: {
    schema: appointmentTypeCreateSchema,
    defaultValues: {
      code: '',
      label: '',
      color: '#b4552f',
      textColor: '#ffffff',
      sortOrder: 0,
      active: true,
    },
    sections: [
      {
        id: 'main',
        title: 'Tipo appuntamento',
        fields: [
          { name: 'code', label: 'Codice', type: 'text', required: true, placeholder: 'VIS' },
          { name: 'label', label: 'Etichetta', type: 'text', required: true },
          colorField('Colore', 'color'),
          colorField('Colore testo', 'textColor'),
          { name: 'sortOrder', label: 'Ordine', type: 'number', min: 0, max: 9999 },
          { name: 'active', label: 'Attivo', type: 'switch' },
        ],
      },
    ],
  },
  detail: {
    title: (row) => `${row.code} · ${row.label}`,
    sections: [
      {
        id: 'main',
        title: 'Dati',
        fields: [
          { id: 'code', label: 'Codice' },
          { id: 'label', label: 'Etichetta' },
          {
            id: 'color',
            label: 'Colore',
            render: (row) => (
              <ColorChip color={row.color} textColor={row.textColor} label={row.label} />
            ),
          },
          { id: 'sortOrder', label: 'Ordine' },
          { id: 'active', label: 'Attivo' },
        ],
      },
    ],
  },
  empty: {
    title: 'Nessun tipo appuntamento',
    description: 'I tipi colorano il calendario. Creane uno per iniziare.',
  },
});
